import { useOngoingShifts } from '../../../../ui/features/ongoingShifts/hooks/useOngoingShifts';
import { useMissionsTranslation } from '../../../../ui/hooks';
import { visuallyHidden } from '@juvare/common-form-ui/utils/visualyHidden';
import { ShiftParticipationDialogContent } from '../../../../ui/features/ongoingShifts/components/ShiftParticipationDialogContent';
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SupervisorAccountIconOutlined from '@mui/icons-material/SupervisorAccountOutlined';
import { Box, Button, CircularProgress, Divider, IconButton, Typography } from '@mui/material';
import React from 'react';
export const OngoingShiftsList = ({ missionId, missionName, missionStatus, ariaLabelledBy }) => {
    const { t } = useMissionsTranslation();
    const { loadMoreOngoingShifts, ongoingShifts, hasMoreOngoingShifts, isValidating } = useOngoingShifts({ missionId });
    const showShiftParticipationDialog = (shift) => {
        showCustomDialog({
            maxWidth: 'lg',
            content: (close) => {
                return (React.createElement(ShiftParticipationDialogContent, { shift: shift, missionName: missionName, missionStatus: missionStatus, onDialogClose: close }));
            },
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { component: 'ul', sx: { listStyle: 'none', padding: 0, margin: 0 }, "aria-labelledby": ariaLabelledBy }, ongoingShifts.rows.map((row, idx) => {
            const iconLabelId = `${row.id}-supervisor-account-icon-label`;
            return (React.createElement(Box, { component: 'li', role: 'button', key: row.id, tabIndex: 0, sx: {
                    backgroundColor: themeColors.surface.secondary,
                    '&:hover': {
                        backgroundColor: (theme) => theme.palette.action.hover,
                        cursor: 'pointer',
                    },
                    '&:focus-visible': {
                        outline: (theme) => '1px solid ' + theme.palette.primary.light,
                    },
                }, onKeyDown: (event) => {
                    if (event.key === 'Enter') {
                        showShiftParticipationDialog(row);
                    }
                    else if (event.key === 'Space') {
                        event.preventDefault();
                        showShiftParticipationDialog(row);
                    }
                }, onClick: () => showShiftParticipationDialog(row), "aria-labelledby": `mission-${missionId}-label ${row.id} ${row.id}-hint` },
                React.createElement(Box, { id: row.id, sx: visuallyHidden },
                    t('date', { value: row.columns.startTime }),
                    " \u2014 ",
                    t('time', { value: row.columns.startTime }),
                    " to",
                    ' ',
                    t('time', { value: row.columns.stopTime }),
                    ", ",
                    row.columns.countyName),
                React.createElement(Box, { id: `${row.id}-hint`, sx: visuallyHidden }, t('shifts.ongoingShifts.rowHint')),
                React.createElement(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: 2,
                        borderRadius: idx === ongoingShifts.rows.length - 1 ? '0 0 4px 4px' : 'none',
                        p: 1.5,
                    } },
                    React.createElement(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' } },
                        React.createElement(Typography, { variant: 'body2' }, t('date', { value: row.columns.startTime })),
                        React.createElement(Divider, { orientation: 'vertical', sx: { color: themeColors.border.main, height: 16 } }),
                        React.createElement(Typography, { variant: 'body2' },
                            t('time', { value: row.columns.startTime }),
                            " - ",
                            t('time', { value: row.columns.stopTime })),
                        React.createElement(Divider, { orientation: 'vertical', sx: { color: themeColors.border.main, height: 16 } }),
                        React.createElement(Typography, { variant: 'body2' }, row.columns.countyName)),
                    React.createElement(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' } },
                        React.createElement(Divider, { orientation: 'vertical', sx: { color: themeColors.border.main, height: 24 } }),
                        React.createElement(Box, { id: iconLabelId, sx: visuallyHidden }, t('shifts.ongoingShifts.showParticipantDialogButtonLabel')),
                        React.createElement(IconButton, { onClick: (e) => {
                                e.stopPropagation();
                                showShiftParticipationDialog(row);
                            }, onKeyDown: (e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    showShiftParticipationDialog(row);
                                }
                            }, "aria-labelledby": 'supervisor-account-icon', sx: { color: themeColors.icon.link } },
                            React.createElement(Box, { id: 'supervisor-account-icon', sx: visuallyHidden }, t('shifts.ongoingShifts.showParticipantDialogButtonLabel')),
                            React.createElement(SupervisorAccountIconOutlined, { sx: { verticalAlign: 'middle', color: themeColors.icon.link } })))),
                (idx < ongoingShifts.rows.length - 1 || (idx === ongoingShifts.rows.length - 1 && hasMoreOngoingShifts)) && (React.createElement(Divider, { sx: { mx: 2, color: themeColors.border.main } }))));
        })),
        isValidating && (React.createElement(Box, { sx: {
                backgroundColor: themeColors.surface.secondary,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 1,
            } },
            React.createElement(CircularProgress, null))),
        hasMoreOngoingShifts && (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', backgroundColor: themeColors.surface.secondary, px: 1, py: 0.5 } },
            React.createElement(Button, { sx: {
                    alignSelf: 'center',
                    width: { xs: '100%', sm: 'auto' },
                }, size: 'small', startIcon: React.createElement(KeyboardArrowDownIcon, null), onClick: loadMoreOngoingShifts, disabled: isValidating, "aria-labelledby": 'show-more-button' },
                React.createElement(Box, { id: 'show-more-button', sx: visuallyHidden }, t('shifts.ongoingShifts.loadMoreShifts')),
                React.createElement(Typography, { variant: 'button', sx: { fontSize: 12 } }, t('missions.showMore')))))));
};
